import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import type { SmartReportRequest } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode } from '@/services/apiClient';
import { useSmartCatalogueQuery, useSmartRunQuery } from '../api/smart.api';
import { ReportTabs } from '../components/ReportTabs';
import { SmartBuilder } from '../smart/SmartBuilder';
import { useSummaryChips } from '../smart/smartSummary';
import { SmartResultView } from '../smart/SmartResult';
import {
  emptyDraft,
  fromRequest,
  toRequest,
  type SmartDraft,
  type SmartStep,
} from '../smart/smartDraft';

/**
 * Reports › Smart Report Builder.
 *
 * A guided conversation that builds one report from choices the server offered. Nothing typed is
 * interpreted and no model is involved: the page composes a request, the server validates it and
 * runs the same engine as the matching tab. The draft lives in this page's memory only — never in
 * the address bar — so a link to this page never carries a report's contents.
 */
export function SmartReportPage() {
  const { t } = useTranslation();
  const catalogue = useSmartCatalogueQuery();
  const [draft, setDraft] = useState<SmartDraft>(emptyDraft);
  const [step, setStep] = useState<SmartStep>('area');
  const [request, setRequest] = useState<SmartReportRequest | null>(null);
  const [building, setBuilding] = useState(true);
  const run = useSmartRunQuery(request);
  const chips = useSummaryChips(
    draft,
    catalogue.data ?? {
      areas: [],
      branches: [],
      baseCurrencyCode: '',
      maxRangeDays: 0,
      quickReports: [],
    },
  );
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (run.data && !building) resultRef.current?.focus();
  }, [run.data, building]);

  // A quick report chosen on the Overview arrives in router state, is applied once, and is cleared so a reload starts fresh.
  const location = useLocation();
  const navigate = useNavigate();
  const handed = (location.state as { prefill?: SmartReportRequest } | null)?.prefill;
  useEffect(() => {
    if (!handed || !catalogue.data) return;
    const next = fromRequest(handed, catalogue.data);
    if (next) {
      setDraft(next);
      setStep('review');
      setBuilding(true);
    }
    navigate('.', { replace: true, state: null });
  }, [handed, catalogue.data, navigate]);

  const generate = (next: SmartDraft = draft) => {
    const built = toRequest(next);
    if (!built) return;
    setRequest(built);
    setBuilding(false);
  };

  const edit = (target: SmartStep) => {
    setStep(target);
    setBuilding(true);
  };

  /** A quick report fills the builder and waits on its summary; the reader confirms it. */
  const prefill = (prepared: SmartReportRequest) => {
    if (!catalogue.data) return;
    const next = fromRequest(prepared, catalogue.data);
    if (!next) return;
    setDraft(next);
    setStep('review');
    setBuilding(true);
  };

  /** A suggested next report was validated for this reader on the server, so it runs at once. */
  const follow = (prepared: SmartReportRequest) => {
    if (!catalogue.data) return;
    const next = fromRequest(prepared, catalogue.data);
    if (!next) return;
    setDraft(next);
    setStep('review');
    generate(next);
  };

  const errorText = () => {
    const code = apiErrorCode(run.error);
    if (code === 'FORBIDDEN') return t('reports.smart.errors.forbidden');
    if (code === 'VALIDATION_FAILED') return t('reports.smart.errors.invalid');
    return t('reports.loadError');
  };

  const data = catalogue.data;
  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.smart.pageTitle')} subtitle={t('reports.smart.pageSubtitle')} />
      <ReportTabs />
      <nav aria-label={t('reports.smart.breadcrumb')} className="text-muted text-sm">
        <Link to="/reports" className="hover:underline">
          {t('reports.tabs.OVERVIEW')}
        </Link>
        <span aria-hidden> / </span>
        <span className="text-ink">{t('reports.smart.pageTitle')}</span>
      </nav>

      {catalogue.isError ? <Alert tone="error">{t('reports.loadError')}</Alert> : null}
      {catalogue.isPending ? (
        <div className="bg-surface border-line h-40 animate-pulse rounded-xl border" aria-hidden />
      ) : null}
      {data && data.areas.length === 0 ? (
        <Alert tone="info">{t('reports.smart.noAreas')}</Alert>
      ) : null}

      {data && data.areas.length > 0 && building ? (
        <SmartBuilder
          catalogue={data}
          draft={draft}
          step={step}
          onDraft={setDraft}
          onStep={setStep}
          onGenerate={() => generate()}
          generating={run.isFetching}
        />
      ) : null}

      {data &&
      data.areas.length > 0 &&
      step === 'area' &&
      building &&
      data.quickReports.length > 0 ? (
        <section aria-labelledby="smart-quick" className="space-y-2">
          <h2 id="smart-quick" className="text-ink flex items-center gap-2 font-semibold">
            <Sparkles className="text-brand-600 size-4" aria-hidden />
            {t('reports.smart.quickTitle')}
          </h2>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {data.quickReports.map((quick) => (
              <button
                key={quick.key}
                type="button"
                onClick={() => prefill(quick.request)}
                className="border-line hover:border-brand-300 rounded-xl border bg-white p-3 text-start text-sm"
              >
                <span className="text-ink block font-medium">
                  {t(`reports.smart.suggestions.${quick.key}`)}
                </span>
                <span className="text-muted block text-xs">
                  {t(`reports.smart.areas.${quick.request.area}`)}
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {request && !building ? (
        <div ref={resultRef} tabIndex={-1} className="space-y-4 outline-none">
          {run.isPending ? <Card className="h-48 animate-pulse" aria-hidden /> : null}
          {run.isError ? <Alert tone="error">{errorText()}</Alert> : null}
          {run.data ? (
            <SmartResultView result={run.data} chips={chips} onEdit={edit} onSuggestion={follow} />
          ) : null}
          <button
            type="button"
            onClick={() => {
              setDraft(emptyDraft());
              setStep('area');
              setRequest(null);
              setBuilding(true);
            }}
            className="text-brand-700 text-sm font-medium hover:underline"
          >
            {t('reports.smart.startOver')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
